import { useQuery } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router";

import { ErrorPage } from "@/components/Error/ErrorPage";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { useProject } from "@/hooks/Network/useProject";
import { usePageEntryState } from "@/hooks/usePageEntryState";
import { useSlug } from "@/hooks/useSlug";
import { addressMutations } from "@/queries/addresses/mutations";
import { useAddressQueries } from "@/queries/addresses/queryOptions";
import { useBuildingQueries } from "@/queries/buildings/queryOptions";
import { routes } from "@/routes";

import { AddressForm, type AddressFormValues, mapAddressFormToRequest } from "../components/AddressForm";

export function CreateAddressPage(): React.ReactNode {
  const slug = useSlug();
  const navigate = useNavigate();
  const project = useProject();
  const buildingQueries = useBuildingQueries();
  const buildingsQuery = useQuery(buildingQueries.list());
  const addressQueries = useAddressQueries();
  const addressesQuery = useQuery(addressQueries.list());
  const createAddress = addressMutations.useCreateAddress();
  const buildings = buildingsQuery.data?.items ?? [];
  const addresses = addressesQuery.data?.items ?? [];
  const values: AddressFormValues = {
    building: buildings.at(0),
    city: project.city ?? "",
    floor: "",
    housenumber: "",
    housenumberSuffix: "",
    postalCode: "",
    streetName: "",
    type: "",
  };
  const form = useForm<AddressFormValues>({
    values,
    resetOptions: { keepDirtyValues: true },
  });
  const pageEntry = usePageEntryState({
    identity: "create-address",
    isFetching: buildingsQuery.isFetching || addressesQuery.isPending,
    isReady: buildingsQuery.data !== undefined && addressesQuery.data !== undefined,
    error: buildingsQuery.error ?? addressesQuery.error,
  });

  if (pageEntry.isPending) {
    return <FullSizeLoader withPadding />;
  }

  if (pageEntry.error) {
    return <ErrorPage error={pageEntry.error} />;
  }

  return (
    <AddressForm
      isEditMode={false}
      form={form}
      isSubmitting={createAddress.isPending}
      buildings={buildings}
      addresses={addresses}
      onSubmit={(formValues) => {
        const payload = mapAddressFormToRequest(formValues);
        if (!payload) {
          return;
        }

        createAddress.mutate(payload, {
          onSuccess: () => {
            void navigate(routes.addresses.list({ slug }));
          },
        });
      }}
    />
  );
}
